<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import WootSnackbar from './Snackbar.vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const STORAGE_KEY = 'cw_crm_overdue_alerts';
const SNOOZE_KEY = 'cw_crm_overdue_snoozed';
const SNOOZE_DURATION = 5 * 60 * 1000; // 5 minutes

const props = defineProps({
  duration: {
    type: Number,
    default: 2500,
  },
});

const { t } = useI18n();
const router = useRouter();

const snackMessages = ref([]);
const crmAlerts = ref([]);
const snackbarContainer = ref(null);

const showPopover = () => {
  try {
    const el = snackbarContainer.value;
    if (el?.matches(':popover-open')) {
      el.hidePopover();
    }
    el?.showPopover();
  } catch (e) {
    // ignore
  }
};

// ---- Regular toast logic ----
const dismissSnack = key => {
  snackMessages.value = snackMessages.value.filter(s => s.key !== key);
};

const onNewToastMessage = ({ message: originalMessage, action }) => {
  const message = action?.usei18n ? t(originalMessage) : originalMessage;
  const duration = action?.duration || props.duration;

  const key = Date.now() + Math.random();
  snackMessages.value.push({ key, message, action, persistent: false });
  nextTick(showPopover);
  setTimeout(() => {
    snackMessages.value = snackMessages.value.filter(s => s.key !== key);
  }, duration);
};

// ---- CRM Overdue task alert logic (persistent + snooze) ----
const addCrmAlert = ({ taskId, message, dealUrl }) => {
  // Deduplicate
  if (crmAlerts.value.some(a => a.taskId === taskId)) return;
  crmAlerts.value.push({ taskId, message, dealUrl });
  nextTick(showPopover);
};

const dismissCrmAlert = taskId => {
  crmAlerts.value = crmAlerts.value.filter(a => a.taskId !== taskId);
  // Remove from localStorage
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    delete stored[taskId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (e) {
    // silent
  }
};

const snoozeCrmAlert = taskId => {
  crmAlerts.value = crmAlerts.value.filter(a => a.taskId !== taskId);
  // Set snooze timestamp in localStorage
  try {
    const snoozed = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}');
    snoozed[taskId] = Date.now() + SNOOZE_DURATION;
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(snoozed));
  } catch (e) {
    // silent
  }
};

const viewDeal = (taskId, dealUrl) => {
  dismissCrmAlert(taskId);
  router.push(dealUrl);
};

const restoreFromLocalStorage = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const snoozed = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}');
    const now = Date.now();

    Object.entries(stored).forEach(([taskId, alertData]) => {
      // Skip snoozed
      if (snoozed[taskId] && now < snoozed[taskId]) return;

      const { task, deal, account_id: accountId } = alertData;
      const message = `⚠️ Tarefa atrasada: "${task.title}" no negócio "${deal.name}"`;
      const dealUrl = `/app/accounts/${accountId}/crm/deals?deal_id=${deal.id}`;
      addCrmAlert({ taskId: Number(taskId), message, dealUrl });
    });

    if (crmAlerts.value.length > 0) {
      nextTick(showPopover);
    }
  } catch (e) {
    // silent
  }
};

onMounted(() => {
  emitter.on('newToastMessage', onNewToastMessage);
  emitter.on('crmTaskOverdue', addCrmAlert);
  // Restore persistent alerts from localStorage on page load
  restoreFromLocalStorage();
});

onUnmounted(() => {
  emitter.off('newToastMessage', onNewToastMessage);
  emitter.off('crmTaskOverdue', addCrmAlert);
});
</script>

<template>
  <div
    ref="snackbarContainer"
    popover="manual"
    class="fixed top-4 left-1/2 -translate-x-1/2 max-w-[28rem] w-[calc(100%-2rem)] text-center bg-transparent border-0 p-0 m-0 outline-none overflow-visible"
    style="z-index: 99999"
  >
    <transition-group name="toast-fade" tag="div">
      <div
        v-if="crmAlerts.length"
        key="crm-group"
        class="mb-2"
      >
        <div
          class="shadow-2xl rounded-2xl flex flex-col gap-3 min-w-[20rem] max-w-[28rem] w-full px-5 py-4 text-left backdrop-blur-xl"
          style="background: rgba(15, 23, 42, 0.92); border: 1px solid rgba(239, 68, 68, 0.3)"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="flex items-center justify-center size-7 rounded-lg bg-red-500/20">
                <span class="i-lucide-alarm-clock size-4 text-red-400" />
              </span>
              <span class="text-sm font-semibold text-white">
                {{ crmAlerts.length }} tarefa{{ crmAlerts.length > 1 ? 's' : '' }} atrasada{{ crmAlerts.length > 1 ? 's' : '' }}
              </span>
            </div>
            <span class="text-[10px] text-slate-400 uppercase tracking-wider font-medium">CRM</span>
          </div>

          <div class="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto">
            <div
              v-for="alert in crmAlerts"
              :key="alert.taskId"
              class="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors cursor-pointer"
              style="background: rgba(255, 255, 255, 0.05)"
              @click="viewDeal(alert.taskId, alert.dealUrl)"
            >
              <span class="shrink-0 size-2 rounded-full bg-red-400 animate-pulse" />
              <span class="flex-1 text-xs text-slate-200 leading-snug line-clamp-2">
                {{ alert.message }}
              </span>
              <span
                class="shrink-0 text-[10px] font-semibold text-red-300 uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Abrir →
              </span>
            </div>
          </div>

          <div class="flex items-center justify-end pt-1 border-t border-white/5">
            <button
              class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium cursor-pointer text-slate-300 transition-all hover:bg-white/10 hover:text-white"
              @click="crmAlerts.forEach(a => snoozeCrmAlert(a.taskId))"
            >
              <span class="i-lucide-bell-off size-3.5" />
              Lembrar em 5 min
            </button>
          </div>
        </div>
      </div>

      <WootSnackbar
        v-for="snackMessage in snackMessages"
        :key="snackMessage.key"
        :message="snackMessage.message"
        :action="snackMessage.action"
        :persistent="false"
        @dismiss="dismissSnack(snackMessage.key)"
      />
    </transition-group>
  </div>
</template>
